'use client';

import {
  Box,
  Flex,
  Grid,
  HStack,
  Icon,
  Progress,
  Text,
  VisuallyHidden,
  VStack,
} from '@chakra-ui/react';
import type { User } from 'firebase/auth';
import { AlertTriangle, Check, Circle, CircleDot } from 'lucide-react';
import { useFirstCollectionProgress } from '@/hooks/useFirstCollectionProgress';
import {
  buildFirstCollectionProgress,
  type FirstCollectionStepState,
} from '@/lib/first-collection-progress';

const STEP_STATE_LABELS: Record<FirstCollectionStepState, string> = {
  complete: 'Complete',
  active: 'In progress',
  pending: 'Upcoming',
  failed: 'Needs attention',
};

function StepStatusIcon({ state }: { state: FirstCollectionStepState }) {
  const styles = {
    complete: {
      bg: 'status.success.bg',
      borderColor: 'status.success.border',
      color: 'status.success.fg',
      icon: <Check />,
    },
    active: {
      bg: 'accent.subtle',
      borderColor: 'ui.border.accent',
      color: 'accent.fg',
      icon: <CircleDot />,
    },
    failed: {
      bg: 'status.danger.bg',
      borderColor: 'status.danger.border',
      color: 'status.danger.fg',
      icon: <AlertTriangle />,
    },
    pending: {
      bg: 'ui.surface.input',
      borderColor: 'ui.border.subtle',
      color: 'ui.text.tertiary',
      icon: <Circle />,
    },
  }[state];

  return (
    <Box
      boxSize="6"
      borderRadius="full"
      bg={styles.bg}
      borderWidth="1px"
      borderColor={styles.borderColor}
      color={styles.color}
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      aria-hidden="true"
    >
      <Icon boxSize="3.5">{styles.icon}</Icon>
    </Box>
  );
}

export function FirstCollectionProgressCard({
  user,
  jobId,
}: {
  user: User | null;
  jobId: string | null;
}) {
  const { workflow, error } = useFirstCollectionProgress({ user, jobId });

  if (!jobId || !workflow || workflow.isComplete) return null;

  const progress = buildFirstCollectionProgress(workflow);
  const hasFailed = Boolean(workflow?.hasFailed);
  const title = hasFailed
    ? 'First data collection needs attention'
    : 'Preparing your first dataset';
  const summary = hasFailed
    ? `Attune could not finish ${progress.activeStep.label.toLowerCase()}. Your workspace is still available.`
    : error
      ? 'Progress updates are delayed. Your collection keeps running in the background.'
      : `Step ${progress.activeStepNumber} of ${progress.steps.length} · ${progress.activeStep.label}`;
  const announcement = hasFailed
    ? `First collection needs attention at step ${progress.activeStepNumber}: ${progress.activeStep.label}.`
    : `First collection step ${progress.activeStepNumber} of ${progress.steps.length}: ${progress.activeStep.label}.`;

  return (
    <Box
      data-section="firstCollectionProgress"
      layerStyle="surfaceCard"
      borderRadius="glassButton"
      borderColor={hasFailed ? 'status.danger.border' : 'ui.border.default'}
      px={{ base: '3.5', md: '4' }}
      py={{ base: '3.5', md: '4' }}
    >
      <VisuallyHidden aria-live="polite">{announcement}</VisuallyHidden>
      <Flex
        align={{ base: 'stretch', md: 'center' }}
        justify="space-between"
        direction={{ base: 'column', md: 'row' }}
        gap="3"
      >
        <HStack gap="3" minW={0} align="center">
          <Box
            boxSize="9"
            borderRadius="glassInput"
            bg={hasFailed ? 'status.danger.bg' : 'ui.surface.selected'}
            borderWidth="1px"
            borderColor={hasFailed ? 'status.danger.border' : 'ui.border.subtle'}
            color={hasFailed ? 'status.danger.fg' : 'ui.text.secondary'}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            aria-hidden="true"
          >
            <Icon boxSize="4">{hasFailed ? <AlertTriangle /> : <CircleDot />}</Icon>
          </Box>
          <Box minW={0}>
            <Text fontSize="sm" fontWeight="800" color="ui.text.primary">
              {title}
            </Text>
            <Text mt="0.5" fontSize="xs" color="ui.text.secondary" lineHeight="1.4">
              {summary}
            </Text>
          </Box>
        </HStack>
        <Box
          alignSelf={{ base: 'flex-start', md: 'center' }}
          borderRadius="glassPill"
          bg={hasFailed ? 'status.danger.bg' : 'ui.surface.input'}
          borderWidth="1px"
          borderColor={hasFailed ? 'status.danger.border' : 'ui.border.subtle'}
          px="2.5"
          py="1"
          flexShrink={0}
        >
          <Text
            fontSize="xs"
            fontWeight="800"
            color={hasFailed ? 'status.danger.fg' : 'ui.text.secondary'}
          >
            Step {progress.activeStepNumber} of {progress.steps.length}
          </Text>
        </Box>
      </Flex>

      <Progress.Root
        mt="3.5"
        value={progress.progressPercent}
        size="sm"
        aria-label="First data collection progress"
        aria-valuetext={`Step ${progress.activeStepNumber} of ${progress.steps.length}: ${progress.activeStep.label}`}
      >
        <Progress.Track bg="ui.surface.selected" borderRadius="full">
          <Progress.Range
            bg={hasFailed ? 'status.danger.fg' : 'accent.solid'}
            borderRadius="full"
            transition="width 300ms ease"
          />
        </Progress.Track>
      </Progress.Root>

      <Grid mt="3.5" templateColumns={{ base: '1fr', md: 'repeat(5, minmax(0, 1fr))' }} gap="2">
        {progress.steps.map((step, index) => (
          <Flex
            key={step.key}
            align={{ base: 'center', md: 'flex-start' }}
            direction={{ base: 'row', md: 'column' }}
            gap="2"
            minW={0}
            minH={{ md: '88px' }}
            px="2.5"
            py="2.5"
            borderRadius="glassInput"
            borderWidth="1px"
            borderColor={
              step.state === 'failed'
                ? 'status.danger.border'
                : step.state === 'active'
                  ? 'ui.border.accent'
                  : step.state === 'complete'
                    ? 'status.success.border'
                    : 'ui.border.subtle'
            }
            bg={
              step.state === 'failed'
                ? 'status.danger.bg'
                : step.state === 'active'
                  ? 'ui.surface.selected'
                  : step.state === 'complete'
                    ? 'status.success.bg'
                    : 'ui.surface.input'
            }
            aria-current={step.state === 'active' ? 'step' : undefined}
          >
            <StepStatusIcon state={step.state} />
            <VStack align="stretch" gap="0.5" minW={0}>
              <Text
                fontSize="11px"
                fontWeight="800"
                color="ui.text.tertiary"
                letterSpacing="0.02em"
              >
                {String(index + 1).padStart(2, '0')}
              </Text>
              <Text
                fontSize="xs"
                fontWeight={step.state === 'active' ? '800' : '650'}
                color={
                  step.state === 'failed'
                    ? 'status.danger.fg'
                    : step.state === 'complete'
                      ? 'status.success.fg'
                      : step.state === 'active'
                        ? 'ui.text.primary'
                        : 'ui.text.secondary'
                }
                lineHeight="1.35"
              >
                {step.label}
              </Text>
              <Text
                fontSize="11px"
                fontWeight="700"
                color={
                  step.state === 'failed'
                    ? 'status.danger.fg'
                    : step.state === 'complete'
                      ? 'status.success.fg'
                      : 'ui.text.tertiary'
                }
              >
                {STEP_STATE_LABELS[step.state]}
              </Text>
            </VStack>
          </Flex>
        ))}
      </Grid>
    </Box>
  );
}
